/**
 * Duplicate a collection under a new name — and, for a seasonal source, a new
 * season window.
 *
 * The shared `PromptDialog` only collects one string, which was enough while a
 * copy could be windowless. Spec 15 R1 gives a seasonal collection no meaning
 * without dates, and the old dates must not be inherited (the copy would read
 * "Ended" on arrival), so the new season is asked for here rather than left as
 * a broken row for the operator to notice later.
 *
 * @see docs/spec/15-store-collections.md §5, §9 Q2
 */
import { useEffect, useState } from "react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
} from "@flint/ui";
import { fromDateInput } from "./collection-format.ts";

export interface DuplicateCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seeds the name field — the caller appends its own "(copy)" convention. */
  defaultName: string;
  /** A core copy needs no dates; a seasonal one cannot be saved without them. */
  seasonal: boolean;
  busy: boolean;
  onConfirm: (values: { name: string; activeFrom: string | null; activeTo: string | null }) => void;
}

export function DuplicateCollectionDialog({
  open,
  onOpenChange,
  defaultName,
  seasonal,
  busy,
  onConfirm,
}: DuplicateCollectionDialogProps) {
  const [name, setName] = useState(defaultName);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  /** Errors stay quiet until the operator has had a go — see below. */
  const [touched, setTouched] = useState(false);

  // Reopening after a failure should offer a clean slate, not the last attempt.
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setFrom("");
      setTo("");
      setTouched(false);
    }
  }, [open, defaultName]);

  const windowMissing = seasonal && (!from || !to);
  const windowBackwards = Boolean(from && to && to < from);
  const error = windowMissing
    ? "Season window is required"
    : windowBackwards
      ? "End date is before start"
      : null;
  const canSubmit = name.trim().length > 0 && !error && !busy;
  // A seasonal dialog opens with an empty window, so `windowMissing` is true on
  // the first render — showing the error then greets the operator with red text
  // for something they haven't had a chance to do yet.
  const showError = error !== null && touched;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate this collection</DialogTitle>
          <DialogDescription>
            {seasonal
              ? "The copy takes the same cover, products and their order. Give the new season its own dates — a duplicate is a new season, so the old ones would make it read “Ended” straight away."
              : "The copy takes the same cover, products and their order."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 px-5 py-1">
          <label className="grid gap-1.5">
            <span className="text-sm font-medium text-ink-2">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>

          {seasonal ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1.5">
                <span className="text-sm font-medium text-ink-2">Season start *</span>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setTouched(true);
                  }}
                />
              </label>
              <label className="grid gap-1.5">
                <span className="text-sm font-medium text-ink-2">Season end *</span>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setTouched(true);
                  }}
                />
              </label>
            </div>
          ) : null}

          {showError ? (
            <p role="alert" className="text-xs text-rose-600">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button intent="secondary" size="md" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            intent="primary"
            size="md"
            disabled={!canSubmit}
            onClick={() =>
              onConfirm({
                name: name.trim(),
                activeFrom: seasonal ? fromDateInput(from) : null,
                activeTo: seasonal ? fromDateInput(to) : null,
              })
            }
          >
            Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
